import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { HelpCircle, CheckCircle2, Clock, Star, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import ConfirmModal from '@/components/shared/ConfirmModal';

const quizzes = [
  { id: 1, title: 'GenAI Fundamentals Quiz', module: 'Module 1', questions: 10, status: 'completed', score: 9, total: 10, duration: '15 min', attempts: 1 },
  { id: 2, title: 'LLM Architecture Assessment', module: 'Module 2', questions: 15, status: 'completed', score: 12, total: 15, duration: '20 min', attempts: 2 },
  { id: 3, title: 'Prompt Engineering Challenge', module: 'Module 2', questions: 12, status: 'available', score: null, total: 12, duration: '18 min', attempts: 0 },
  { id: 4, title: 'API Integration Test', module: 'Module 3', questions: 10, status: 'locked', score: null, total: 10, duration: '15 min', attempts: 0 },
  { id: 5, title: 'Final Module Quiz', module: 'Module 4', questions: 20, status: 'locked', score: null, total: 20, duration: '30 min', attempts: 0 },
];

const statusStyles = {
  completed: { badge: 'bg-green-100 text-green-700', btn: 'outline' },
  available: { badge: 'bg-primary/10 text-primary', btn: 'default' },
  locked: { badge: 'bg-muted text-muted-foreground', btn: 'ghost' },
};

export default function QuizzesSection() {
  const [pendingQuiz, setPendingQuiz] = useState(null);
  const completed = quizzes.filter(q => q.status === 'completed');
  const avgScore = completed.length
    ? Math.round(completed.reduce((s, q) => s + (q.score / q.total) * 100, 0) / completed.length)
    : 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">Quizzes</h2>
          <p className="text-muted-foreground text-sm mt-1">{completed.length}/{quizzes.length} completed · Avg score: {avgScore}%</p>
        </div>
        <div className="flex items-center gap-2 glass rounded-xl px-4 py-2 border border-white/80">
          <Star className="w-4 h-4 text-primary fill-primary" />
          <span className="text-sm font-bold">Avg: {avgScore}%</span>
        </div>
      </div>

      <div className="grid gap-4">
        {quizzes.map((quiz, i) => {
          const s = statusStyles[quiz.status];
          const pct = quiz.score !== null ? Math.round((quiz.score / quiz.total) * 100) : 0;
          return (
            <motion.div key={quiz.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }}
              whileHover={quiz.status !== 'locked' ? { y: -2, transition: { duration: 0.2 } } : {}}
              className={`glass rounded-2xl border border-white/80 p-5 ${quiz.status === 'locked' ? 'opacity-60' : ''}`}>
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${quiz.status === 'completed' ? 'bg-green-100' : quiz.status === 'available' ? 'bg-primary/15' : 'bg-muted'}`}>
                  {quiz.status === 'completed' ? <CheckCircle2 className="w-5 h-5 text-green-500" /> :
                   quiz.status === 'available' ? <Play className="w-5 h-5 text-primary" /> :
                   <HelpCircle className="w-5 h-5 text-muted-foreground" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-heading font-bold text-sm">{quiz.title}</h3>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full capitalize ${s.badge}`}>
                      {quiz.status}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-3 mt-1 text-xs text-muted-foreground">
                    <span>{quiz.module}</span>
                    <span className="flex items-center gap-1"><HelpCircle className="w-3 h-3" />{quiz.questions} questions</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{quiz.duration}</span>
                    {quiz.attempts > 0 && <span>{quiz.attempts} attempt{quiz.attempts > 1 ? 's' : ''}</span>}
                  </div>
                  {quiz.status === 'completed' && (
                    <div className="mt-3 flex items-center gap-3">
                      <Progress value={pct} className="flex-1 h-1.5" />
                      <span className={`text-xs font-bold ${pct >= 80 ? 'text-green-600' : pct >= 60 ? 'text-primary' : 'text-red-500'}`}>
                        {quiz.score}/{quiz.total} ({pct}%)
                      </span>
                    </div>
                  )}
                </div>
                <Button size="sm"
                  onClick={() => quiz.status === 'available' && setPendingQuiz(quiz)}
                  variant={s.btn === 'outline' ? 'outline' : s.btn === 'ghost' ? 'ghost' : 'default'}
                  disabled={quiz.status === 'locked'}
                  className={`rounded-xl text-xs font-semibold flex-shrink-0 gap-1.5 ${s.btn === 'default' ? 'bg-primary text-primary-foreground shadow-md shadow-primary/25 hover:bg-primary/90' : ''}`}>
                  {quiz.status === 'completed' ? 'Review' : quiz.status === 'available' ? <><Play className="w-3.5 h-3.5" />Start Quiz</> : '🔒 Locked'}
                </Button>
              </div>
            </motion.div>
          );
        })}
      </div>
      <ConfirmModal
        open={!!pendingQuiz}
        title="Start Quiz?"
        description={`Ready to start "${pendingQuiz?.title}"? You'll have ${pendingQuiz?.duration} to complete ${pendingQuiz?.questions} questions.`}
        confirmLabel="Yes, Start Quiz"
        onConfirm={() => setPendingQuiz(null)}
        onCancel={() => setPendingQuiz(null)}
      />
    </div>
  );
}